import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/**
 * When landing on /study/:slug?focus=guideline|survey, wait for a completed plan
 * then trigger the matching generation flow once.
 */
export function useStudyFocusGeneration({
  enabled = true,
  locationSearch,
  studySlug,
  artifacts,
  getArtifactStatus,
  openMethodologyModal,
  handleGenerateSurvey,
}) {
  const navigate = useNavigate();
  const triggeredRef = useRef(false);
  const actionsRef = useRef({
    getArtifactStatus,
    openMethodologyModal,
    handleGenerateSurvey,
  });

  actionsRef.current = {
    getArtifactStatus,
    openMethodologyModal,
    handleGenerateSurvey,
  };

  useEffect(() => {
    triggeredRef.current = false;
  }, [studySlug]);

  useEffect(() => {
    if (!enabled || !studySlug || artifacts == null || triggeredRef.current) return;

    const params = new URLSearchParams(locationSearch || "");
    const focus = params.get("focus");
    if (focus !== "guideline" && focus !== "survey") return;

    const { getArtifactStatus: statusOf, openMethodologyModal: openGuide, handleGenerateSurvey: genSurvey } =
      actionsRef.current;

    const planStatus = statusOf("plan");
    if (planStatus === "unknown" || planStatus === "pending" || planStatus === "none") return;

    const targetStatus = statusOf(focus);
    if (targetStatus === "unknown") return;

    triggeredRef.current = true;
    params.delete("focus");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: `/study/${studySlug}`,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );

    if (targetStatus === "completed" || targetStatus === "pending") {
      return;
    }

    if (focus === "guideline") {
      openGuide();
    } else if (focus === "survey") {
      genSurvey();
    }
  }, [artifacts, enabled, locationSearch, navigate, studySlug]);
}
